import React, { createContext, useContext } from "react";
import * as Sentry from "@sentry/react";
import { has } from "lodash";

let CURRENT_USER_TOKEN = "";

/**
 * The React Context object for user data
 */
const UserContext = createContext({});

/**
 * Convenience hook for accessing current user information
 */
const useUserInfo = () => useContext(UserContext);

/**
 * Only operates in the Lexis+ environment.
 *
 * @returns The user object from the Lexis framework, if it exists
 */
function getUserObjectFromLexisFramework() {
  const user = window.Injector.get(
    "page.model.collections.componentmodels.collections.featureproviders",
  ).find((x) => (x.props.type = "ln.advance.litigationanalytics")).ViewModel;
  return user;
}

/**
 *
 * The default testing object is NOT able to be validated by the production API
 * and will result in authentication failures. If you need a valid testing object,
 * in production or testing environments, outside of the Lexis framework, you will
 * need to inject a valid token via the DANGEROUSLY_setTestingToken function.
 *
 * @returns The user object from the testing environment, if it exists
 */
function getUserObjectFromTesting() {
  if (window.LEX_CONSTANT_TESTING_USER) {
    return window.LEX_CONSTANT_TESTING_USER;
  }
}

/**
 * The danager of this function is that it sets a token globally on the window object,
 *  and depending on the timing of that call may have unexpected effects.
 *
 * @param {*} token an optional token to set for testing purposes, used only in development
 */
function DANGEROUSLY_setTestingToken(token) {
  window.LEX_CONSTANT_TESTING_USER.token = token;
}

function setSentryUserInfo(user) {
  Sentry.setUser({
    id: user.userPermId,
    email: user.primaryEmail,
  });
}

function setCurrentUserToken(user) {
  if (has(user, "token")) {
    CURRENT_USER_TOKEN = user.token;
  }
}

/**
 * Provides user data to the component hierarchy to which it is applied, and
 * encapsulates API service call logic.
 */
function UserProvider({ children }) {
  let user;
  try {
    user = getUserObjectFromLexisFramework();
  } catch {
    user = getUserObjectFromTesting();
  } finally {
    setSentryUserInfo(user);
    setCurrentUserToken(user);
  }
  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
}

export {
  UserProvider,
  useUserInfo,
  CURRENT_USER_TOKEN,
  DANGEROUSLY_setTestingToken,
};
